import { FLOW_GRAPH } from "./chat-message";
import { UUID } from "crypto";

export interface IStreamingCompletionResponse {
  onChunk(callback: (chunk: string) => void): Promise<string>;
}

export class StreamingCompletionResponse implements IStreamingCompletionResponse {
  private streamingFetchResponse: Response;

  constructor(streamingFetchResponse: Response) {
    this.streamingFetchResponse = streamingFetchResponse;
  }

  async onChunk(callback: (chunk: string) => void): Promise<string> {
    if (!this.streamingFetchResponse.body) {
      throw new Error('Response body is not available for streaming.');
    }

    const reader = this.streamingFetchResponse.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let content = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        const chunk = decoder.decode(value, { stream: true });
        callback(chunk);
        content += chunk;
      }
    } catch (error) {
      console.error('Error while reading stream:', error);
      throw error;
    } finally {
      reader.releaseLock();
    }

    return content;
  }
}

export class SimulatedStreamingResponse implements IStreamingCompletionResponse {
  private content: string;
  private chunkSize: number;
  private delayMs: number;

  constructor(content: string, chunkSize: number = 5, delayMs: number = 30) {
    this.content = content;
    this.chunkSize = chunkSize;
    this.delayMs = delayMs;
  }

  async onChunk(callback: (chunk: string) => void): Promise<string> {
    let currentIndex = 0;
    const streamChunks = async () => {
      while (currentIndex < this.content.length) {
        const chunk = this.content.substring(currentIndex, Math.min(currentIndex + this.chunkSize, this.content.length));
        callback(chunk);
        currentIndex += this.chunkSize;
        if (currentIndex < this.content.length) {
          await new Promise(resolve => setTimeout(resolve, this.delayMs));
        }
      }
    };

    await streamChunks();
    return this.content;
  }
}

export class EmulatedFlowCompletionResponse extends SimulatedStreamingResponse {
  constructor(flowId: UUID) {
    const currentNode = FLOW_GRAPH[flowId];
    if (!currentNode) {
      throw new Error(`Flow node with ID ${flowId} not found.`);
    }
    const content = currentNode.content;
    super(content, 5, 30);
  }
}
